// src/modules/treasury/components/BranchSummaryTable.jsx
// Branch-wise treasury day summary table used on the HO Dashboard.

import React from 'react';
import { Table, Tag, Space, Typography, Badge } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DAY_STATUS } from '../constants/treasuryConstants';

const { Text } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const STATUS_CONFIG = {
  NO_DAY:                               { color: 'default', label: 'No Activity',         icon: <MinusCircleOutlined /> },
  [DAY_STATUS.OPEN]:                    { color: 'green',   label: 'Open',                icon: <CheckCircleOutlined /> },
  [DAY_STATUS.REOPENED]:                { color: 'purple',  label: 'Reopened',            icon: <CheckCircleOutlined /> },
  [DAY_STATUS.PENDING_CASH_VERIFICATION]: { color: 'orange', label: 'Pending Verification', icon: <ClockCircleOutlined /> },
  [DAY_STATUS.PENDING_CHECKER_APPROVAL]:  { color: 'blue',   label: 'Pending Approval',     icon: <ClockCircleOutlined /> },
  [DAY_STATUS.CLOSED]:                  { color: 'default', label: 'Closed',              icon: <CloseCircleOutlined /> },
};

/**
 * @param {array}   rows        Array from GET /ho-dashboard/branches .data
 * @param {boolean} loading
 */
export default function BranchSummaryTable({ rows = [], loading = false }) {
  const columns = [
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{row.branch?.name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.branch?.code}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'dayStatus',
      width: 170,
      render: (v) => {
        const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.NO_DAY;
        return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
      },
      filters: Object.entries(STATUS_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (value, row) => row.dayStatus === value,
    },
    {
      title: 'Opening',
      dataIndex: ['summary', 'openingCash'],
      width: 120,
      align: 'right',
      render: (v) => v > 0 ? `₹${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Cash In',
      dataIndex: ['summary', 'totalCashIn'],
      width: 120,
      align: 'right',
      render: (v) => v > 0
        ? <Text style={{ color: '#389e0d' }}>₹{fmt(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Cash Out',
      dataIndex: ['summary', 'totalCashOut'],
      width: 120,
      align: 'right',
      render: (v) => v > 0
        ? <Text style={{ color: '#cf1322' }}>₹{fmt(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Closing',
      dataIndex: ['summary', 'closingBalance'],
      width: 120,
      align: 'right',
      render: (v, row) => {
        if (row.dayStatus === 'NO_DAY') return <Text type="secondary">—</Text>;
        return <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>₹{fmt(v)}</Text>;
      },
    },
    {
      title: 'Physical Cash',
      dataIndex: ['businessDay', 'denominationCount', 'totalPhysicalCash'],
      width: 120,
      align: 'right',
      render: (v, row) => {
        if (!row.businessDay) return <Text type="secondary">—</Text>;
        const phys = row.businessDay?.denominationCount?.totalPhysicalCash || 0;
        return phys > 0 ? `₹${fmt(phys)}` : <Text type="secondary">Not counted</Text>;
      },
    },
    {
      title: 'Difference',
      dataIndex: ['businessDay', 'cashDifference'],
      width: 100,
      align: 'right',
      render: (v, row) => {
        if (!row.businessDay) return <Text type="secondary">—</Text>;
        const diff = row.businessDay?.cashDifference || 0;
        if (diff === 0 && row.businessDay?.denominationCount?.totalPhysicalCash > 0) {
          return <Text style={{ color: '#389e0d' }}>₹0</Text>;
        }
        if (diff === 0) return <Text type="secondary">—</Text>;
        return <Text style={{ color: '#cf1322', fontWeight: 500 }}>{diff > 0 ? '+' : ''}₹{fmt(Math.abs(diff))}</Text>;
      },
    },
    {
      title: 'Pending Pmts',
      dataIndex: 'pendingPayments',
      width: 110,
      align: 'center',
      render: (v) => v > 0
        ? <Badge count={v} style={{ backgroundColor: '#fa8c16' }} />
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Opened At',
      dataIndex: ['businessDay', 'openedAt'],
      width: 90,
      render: (v) => v ? dayjs(v).format('HH:mm') : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <Table
      dataSource={rows}
      columns={columns}
      rowKey={(row) => row.branch?._id}
      loading={loading}
      size="small"
      scroll={{ x: 1200 }}
      pagination={false}
      rowClassName={(row) => (row.dayStatus === DAY_STATUS.PENDING_CHECKER_APPROVAL ? 'ant-table-row-pending' : '')}
      locale={{ emptyText: 'No branch data' }}
    />
  );
}
